import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    Download,
    ChevronDown,
    Briefcase,
    MapPin,
    Loader,
    Eye,
    RotateCcw,
} from "lucide-react";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import {
    adminMissionDecisionRequest,
    getAllMissionRequestsRequest,
} from "../../../../redux/actions/missionRequestActions";
import { calculateDurationDays } from "../../Mesrequetes/MesrequetesJS/MissionInlineForm";
import "../TraitementStyles/TraitementOrdreMission.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { exportTreatmentExcel } from "./exportTreatmentExcel";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import { getRequestTreatedBy } from "./treatmentDecisionInfo";
import { API_BASE_URL } from "../../../../../api";
import missionPdfLogo from "../../../../images/cicadaHrMissionLogo.png";

const addMissionPdfBranding = async (doc) => {
    const logo = new Image();
    logo.src = missionPdfLogo;
    await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = reject;
    });
    doc.addImage(logo, "PNG", 20, 17, 52, 13);
};

/* ── SearchableCombobox ── */
const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Effacer">
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Afficher la liste">
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    Aucun résultat
                </div>
            )}
        </div>
    );
};

const TraitementMissions = () => {
    const dispatch = useDispatch();

    // Redux state - Ã  adapter selon ton slice exact
    const { allRequests, loading } = useSelector((state) => state.missionRequests || {
        allRequests: [],
        loading: false,
    });
    const demandes = allRequests || [];

    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAllMissionRequestsRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(getAllMissionRequestsRequest({ silent: true }));
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const getMissionLocation = (demande) =>
        demande?.ordreMissionLieu || demande?.ordreMissionVille || demande?.ordreMissionAdresse || demande?.ordreMissionPays || "";

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente du manager", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "En attente de l'admin RH",    bg: "#fff3cd", color: "#856404" },
            APPROVED:        { label: "Approuvé",       bg: "#d4edda", color: "#155724" },
            REJECTED:        { label: "Rejeté",         bg: "#f8d7da", color: "#721c24" },
            CANCELLED:       { label: "Annulé",         bg: "#e2e8f0", color: "#4b5563" },
        };
        const c = config[statut] || config.PENDING_ADMIN;
        return (
            <span className="tomBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const handleApprove = (demandeId) => {
        confirmTreatmentApproval(() => dispatch(adminMissionDecisionRequest({
            id: demandeId,
            decision: true,
            comment: ""
        })));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;

        dispatch(adminMissionDecisionRequest({
            id: selectedDemande.id,
            decision: false,
            comment: motifRejet
        }));

        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    // ── Génération PDF ordre de mission ──
    const handleDownloadPdf = async (demande) => {
        console.log("DEMANDE OBJET COMPLET :", demande);
        try {
            const doc = new jsPDF();
            await addMissionPdfBranding(doc);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(42, 92, 169);
            doc.text("ORDRE DE MISSION", 20, 68);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(14);
            doc.setTextColor(42, 92, 169);
            doc.text(`Ref. ${demande.ordreMissionCode || "N/A"}`, 20, 76);

            let y = 91;

            const addSectionTitle = (title) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(42, 92, 169);
                doc.text(title, 20, y);
                y += 7;
            };

            const addField = (label, value) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const labelText = label + " : ";
                doc.text(labelText, 20, y);
                const labelWidth = doc.getTextWidth(labelText);
                doc.setFont("helvetica", "normal");
                doc.text(value || "", 20 + labelWidth, y);
                y += 6;
            };

            const addTextParagraph = (text) => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const splitText = doc.splitTextToSize(text || "", 170);
                doc.text(splitText, 20, y);
                y += (splitText.length * 5) + 3;
            };

            // Section 1 : Collaborateur
            addSectionTitle("1. Identification du collaborateur");
            addField("Nom et prénom", demande.employeeName || "Collaborateur");
            addField("Fonction", demande.employeeJobName || "-");
            addField("CIN / Passeport", demande.employeeCin || demande.employeePasseport || "-");
            y += 4;

            // Section 2 : Objet
            addSectionTitle("2. Objet de la mission");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("Intitulé / objectif de la mission :", 20, y);
            y += 5;
            addTextParagraph(
                demande.ordreMissionLibelle +
                (demande.ordreMissionObjet ? ` - ${demande.ordreMissionObjet}` : "")
            );
            y += 4;

            // Section 3 : Lieu & durée
            addSectionTitle("3. Lieu et durée de la mission");
            const lieuParts = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse].filter(Boolean);
            const lieuText = lieuParts.length > 0 ? lieuParts.join(", ") : (demande.ordreMissionLieu || "-");
            addField("Lieu de la mission", lieuText);
            addField("Date de départ", formatDate(demande.ordreMissionDateDebut));
            addField("Date de retour", formatDate(demande.ordreMissionDateFin));
            const duration = calculateDurationDays(demande.ordreMissionDateDebut, demande.ordreMissionDateFin);
            addField("Durée totale", duration ? `${duration} jour${duration > 1 ? "s" : ""}` : "-");
            y += 4;

            // Section 4 : Prise en charge
            addSectionTitle("4. Prise en charge et conditions");
            addTextParagraph(
                "Les frais liés à la mission (transport, hébergement, restauration et autres frais professionnels) " +
                "sont pris en charge par la société, conformément à la politique interne et sur présentation des justificatifs."
            );
            y += 4;

            // Section 5 : Engagement
            addSectionTitle("5. Engagement collaborateur");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("Le collaborateur s'engage à :", 20, y);
            y += 6;
            addTextParagraph(demande.ordreMissionEngagementsCollaborateur || "-");
            y += 5;

            // Sign/Footer
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.text(
                `Fait à Agadir, le ${new Date(demande.createdAt).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric"
                })}`,
                120,
                y
            );

            doc.save(`Ordre_Mission_${demande.ordreMissionCode || demande.id}.pdf`);
        } catch (err) {
            console.error("PDF generation error", err);
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Une erreur est survenue lors de la génération du PDF.",
                confirmButtonColor: "#ef4444",
            });
        }
    };

    const mapStatutToTab = (statut) => {
        if (statut === "PENDING_ADMIN") return "enCours";
        if (statut === "APPROVED") return "approuve";
        return "";
    };

    const isRejectedByAdmin = (demande) =>
        demande?.status === "REJECTED" && demande?.adminDecision === false;

    const collaborateurOptions = useMemo(() => {
        if (!demandes) return [];
        const map = new Map();
        demandes.forEach((d) => {
            const name = d.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: d.employeeMatricule ? `${name} (${d.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [demandes]);

    const typeOptions = useMemo(() => {
        if (!demandes) return [];
        return [...new Set(demandes.map((d) => d.ordreMissionLibelle).filter(Boolean))]
            .sort()
            .map((t) => ({ value: t, label: t }));
    }, [demandes]);

    const getFilteredDemandes = () => {
        if (!demandes || demandes.length === 0) return [];

        let filtered = demandes.filter((d) =>
            activeTab === "rejete"
                ? isRejectedByAdmin(d)
                : mapStatutToTab(d.status) === activeTab
        );

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.employeeName || "").toLowerCase().includes(val) ||
                    (d.employeeMatricule || "").toLowerCase().includes(val)
            );
        }

        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((d) => (d.ordreMissionLibelle || "").toLowerCase().includes(val));
        }

        if (filterDate) {
            filtered = filtered.filter((d) => {
                if (!d.createdAt) return false;
                return new Date(d.createdAt).toISOString().split('T')[0] === filterDate;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        if (!demandes) return [
            { id: "enCours",  label: "En cours",  count: 0 },
            { id: "approuve", label: "Approuvées", count: 0 },
            { id: "rejete",   label: "Rejetées",  count: 0 },
        ];

        return [
            {
                id: "enCours",
                label: "En cours",
                count: demandes.filter(d => d.status === "PENDING_ADMIN").length
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: demandes.filter(d => d.status === "APPROVED").length
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: demandes.filter(isRejectedByAdmin).length
            },
        ];
    }, [demandes]);

    const workflowsDisponibles = useMemo(() => {
        if (!demandes) return [];
        return [...new Set(demandes.map(d => d.workflowLevel).filter(Boolean))];
    }, [demandes]);

    const filtered = getFilteredDemandes();

    if (loading && (!demandes || demandes.length === 0)) {
        return (
            <div className="tomPage">
                <div className="tomWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tomPage">
            <div className="tomWrap">
                {/* Header */}
                <div className="tomHeader">
                    <div>
                        <h1>Traitement des demandes de mission</h1>
                        <p>Gérez et validez les demandes de participation aux ordres de mission</p>
                    </div>
                </div>

                {/* Filters Panel */}
                <div className="tomCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Collaborateur / Matricule
                            </label>
                            <SearchableCombobox
                                value={filterCollab}
                                onChange={setFilterCollab}
                                options={collaborateurOptions}
                                placeholder="Nom ou matricule..."
                            />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Mission
                            </label>
                            <SearchableCombobox
                                value={filterType}
                                onChange={setFilterType}
                                options={typeOptions}
                                placeholder="Rechercher une mission..."
                            />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Date de la demande
                            </label>
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="dcInput"
                                style={{ width: "100%", fontSize: "13px" }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => exportTreatmentExcel(filtered, "Ordres_de_mission_admin")}><Download size={14} /> Exporter Excel</button>
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="dcBtn dcBtn--outline"
                                style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}
                            >
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tomCard tomCard--noPad">
                    <div className="tomTabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tomTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tomCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tomTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tomEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tomTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Mission</th>
                                    <th>Période</th>
                                    <th>Lieu</th>
                                    {/*<th>Justificatifs</th>*/}
                                    <th>Date de la demande</th>
                                    {(activeTab === "approuve" || activeTab === "rejete") && <th>Traitée par</th>}
                                    {activeTab === "rejete" && <th>Motif</th>}
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tomRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? "Demande urgente" : ""}
                                    >
                                        <td>
                                            <span className="tomPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>

                                        <td>
                                            <div className="tomName">
                                                <span className="tomPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tomFire">🔥</span>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tomType">
                                                <Briefcase size={16} />
                                                <div>
                                                    <div className="tomPrimary">{demande.ordreMissionLibelle}</div>
                                                    <div className="tomSub">{demande.ordreMissionCode}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tomPeriod">
                                                <div>{formatDate(demande.ordreMissionDateDebut)}</div>
                                                <div>- {formatDate(demande.ordreMissionDateFin)}</div>
                                            </div>
                                        </td>

                                        <td>
                                            {getMissionLocation(demande) ? (
                                                <div className="tomLocation">
                                                    <MapPin size={14} />
                                                    {getMissionLocation(demande)}
                                                </div>
                                            ) : "—"}
                                        </td>

                                        {/*<td>*/}
                                        {/*    <div className="tomJustif">*/}
                                        {/*        <FileText size={14} />*/}
                                        {/*        <span>{demande.justificatifs?.length || 0}</span>*/}
                                        {/*    </div>*/}
                                        {/*</td>*/}

                                        <td>
                                            <span className="tomMuted">{formatDate(demande.createdAt)}</span>
                                        </td>

                                        {(activeTab === "approuve" || activeTab === "rejete") && (
                                            <td><span className="tomMuted">{getRequestTreatedBy(demande)}</span></td>
                                        )}

                                        {activeTab === "rejete" && (
                                            <td>
                                                <span className="tomMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {demande.adminComment || demande.managerComment || "—"}
                                                </span>
                                            </td>
                                        )}

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="tomRowActions">
                                                {demande.status === "PENDING_ADMIN" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tomActionBtn tomActionBtn--ok"
                                                            onClick={() => handleApprove(demande.id)}
                                                            title="Approuver"
                                                            disabled={loading}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tomActionBtn tomActionBtn--no"
                                                            onClick={() => handleReject(demande)}
                                                            title="Refuser"
                                                            disabled={loading}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                {demande.status === "APPROVED" && (
                                                    <button
                                                        type="button"
                                                        className="tomActionBtn"
                                                        onClick={() => handleDownloadPdf(demande)}
                                                        title="Télécharger Ordre de Mission PDF"
                                                        style={{ background: "#e0e7ff", color: "#3730a3" }}
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="tomIconBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title="Voir détails"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
                MODAL DÉTAILS
            â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {showDetailsModal && selectedDemande && (
                <div className="tomModal">
                    <div className="tomModal__panel" role="dialog" aria-modal="true">
                        <div className="tomModal__header">
                            <div className="tomModal__headLeft">
                                <div className="tomModal__icon">
                                    <Briefcase size={18} />
                                </div>
                                <div>
                                    <div className="tomModal__title">Demande de mission #{selectedDemande.id}</div>
                                    <div className="tomModal__sub">Demandée le {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>
                            <button type="button" className="tomIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tomModal__body">
                            <div className="tomGrid2">
                                {/* Collaborateur */}
                                <div className="tomPanel">
                                    <div className="tomPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tomKv"><span>Matricule</span><b>{selectedDemande.employeeMatricule || "â€”"}</b></div>
                                    <div className="tomKv"><span>Nom</span><b>{selectedDemande.employeeName}</b></div>
                                </div>

                                {/* Mission */}
                                <div className="tomPanel">
                                    <div className="tomPanel__title">
                                        <Briefcase size={16} /> Mission
                                    </div>
                                    <div className="tomKv"><span>Libellé</span><b>{selectedDemande.ordreMissionLibelle}</b></div>
                                    <div className="tomKv"><span>Code</span><b>{selectedDemande.ordreMissionCode}</b></div>
                                    <div className="tomKv"><span>Période</span>
                                        <b>{formatDate(selectedDemande.ordreMissionDateDebut)} - {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                    </div>
                                    {getMissionLocation(selectedDemande) && (
                                        <div className="tomKv"><span>Lieu</span><b>{getMissionLocation(selectedDemande)}</b></div>
                                    )}
                                    <div className="tomKv"><span>Workflow</span>
                                        <b>{selectedDemande.workflowLevel === "both" ? "Manager + RH" :
                                            selectedDemande.workflowLevel === "manager" ? "Manager" : "RH"}</b>
                                    </div>
                                </div>

                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tomPanel tomPanel--full">
                                        <div className="tomPanel__title">
                                            <FileText size={16} /> Justificatifs ({selectedDemande.justificatifs.length})
                                        </div>
                                        <div className="tomDocs">
                                            {selectedDemande.justificatifs.map((j, idx) => (
                                                <a key={idx} className="tomDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                    <FileText size={18} />
                                                    <span>{j}</span>
                                                    <Download size={16} />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Motif */}
                                {/*<div className="tomPanel tomPanel--full">*/}
                                {/*    <div className="tomPanel__title">*/}
                                {/*        <FileText size={16} /> Motif de la demande*/}
                                {/*    </div>*/}
                                {/*    <div className="tomText">{selectedDemande.motif || " - "}</div>*/}
                                {/*</div>*/}

                                {/* Décision Manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tomPanel tomPanel--full">
                                        <div className="tomPanel__title">
                                            <Check size={16} /> Décision Manager
                                        </div>
                                        <div className="tomKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && <div className="tomKv"><span>Par</span><b>{selectedDemande.managerName}</b></div>}
                                        {selectedDemande.managerDecisionAt && <div className="tomKv"><span>Date</span><b>{formatDate(selectedDemande.managerDecisionAt)}</b></div>}
                                        {selectedDemande.managerComment && (
                                            <div className="tomRejectBox">
                                                <div className="tomRejectLabel">Commentaire</div>
                                                <div className="tomRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Décision RH */}
                                {selectedDemande.status !== "PENDING_MANAGER" && selectedDemande.adminDecision !== null && (
                                    <div className={`tomPanel tomPanel--full ${selectedDemande.status === "APPROVED" ? "tomValidOk" : "tomValidNo"}`}>
                                        <div className="tomPanel__title">
                                            {selectedDemande.status === "APPROVED" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.status === "APPROVED" ? " Mission approuvée" : " Mission refusée"}
                                        </div>
                                        <div className="tomKv"><span>Date</span><b>{formatDate(selectedDemande.adminDecisionAt)}</b></div>
                                        {selectedDemande.adminName && <div className="tomKv"><span>Par</span><b>{selectedDemande.adminName}</b></div>}
                                        {selectedDemande.adminComment && (
                                            <div className="tomRejectBox">
                                                <div className="tomRejectLabel">
                                                    {selectedDemande.status === "APPROVED" ? "Commentaire" : "Motif du refus"}
                                                </div>
                                                <div className="tomRejectText">{selectedDemande.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tomModal__footer">
                            {selectedDemande.status === "APPROVED" && (
                                <button
                                    className="tomBtn tomBtn--pdf"
                                    type="button"
                                    onClick={() => handleDownloadPdf(selectedDemande)}
                                >
                                    <Download size={15} /> Télécharger l'ordre de mission
                                </button>
                            )}
                            {selectedDemande.status === "PENDING_ADMIN" ? (
                                <>
                                    <button
                                        className="tomBtn tomBtn--danger"
                                        onClick={() => { closeDetails(); handleReject(selectedDemande); }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        className="tomBtn tomBtn--ok"
                                        onClick={() => { handleApprove(selectedDemande.id); closeDetails(); }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button className="tomBtn tomBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button className="tomBtn tomBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
                MODAL REJET
            â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {showRejectModal && selectedDemande && (
                <div className="tomModal">
                    <div className="tomModal__panel tomModal__panel--small">
                        <div className="tomModal__header">
                            <div className="tomModal__headLeft">
                                <div className="tomModal__icon tomModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tomModal__title">Refuser la demande de mission</div>
                                    <div className="tomModal__sub">
                                        {selectedDemande.employeeName} â€” {selectedDemande.ordreMissionLibelle}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tomIconBtn" onClick={closeReject}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tomModal__body">
                            <div className="tomMiniCard">
                                <div className="tomMiniRow">
                                    <span className="tomMuted">Mission</span>
                                    <b>{selectedDemande.ordreMissionCode} â€“ {selectedDemande.ordreMissionLibelle}</b>
                                </div>
                                <div className="tomMiniRow">
                                    <span className="tomMuted">Période</span>
                                    <b>{formatDate(selectedDemande.ordreMissionDateDebut)} - {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                </div>
                            </div>

                            <label className="tomLabel">
                                Motif du refus <span className="tomReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Expliquez pourquoi cette demande est refusée..."
                                className="tomTextarea"
                            />
                            <div className="tomHelp">Ce motif sera visible par le collaborateur</div>
                        </div>

                        <div className="tomModal__footer">
                            <button type="button" className="tomBtn tomBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tomBtn tomBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementMissions;
